import axios from "axios";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import baseURL from "../../config/api";

interface KeywordItem {
    id: number;
    keywords: string;
    path: string;
}

const HelpDocHeader: React.FC = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [results, setResults] = useState<KeywordItem[]>([]);
    const [page, setPage] = useState<number>(0);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    const observer = useRef<IntersectionObserver | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const fetchSearchResults = async (pageNumber: number, keyword: string = "") => {
        try {
            setLoading(true);
            const endpoint = keyword.trim()
                ? `keyword/GetKeywordSearchsByKewordPaging?keyword=${encodeURIComponent(
                    keyword
                )}&page=${pageNumber}&size=10`
                : `keyword/GetKeywordSearchsPaging/${pageNumber},10`;

            const response = await axios.get(`${baseURL}${endpoint}`);

            setResults((prev) =>
                pageNumber === 0
                    ? response.data.keywords || []
                    : [...prev, ...(response.data.keywords || [])]
            );
            setHasMore((response.data.keywords || []).length === 10);
        } catch (error) {
            console.error("Error fetching search results:", error);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!dropdownOpen) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);

        debounceRef.current = setTimeout(() => {
            setPage(0);
            fetchSearchResults(0, searchTerm);
        }, 500);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, dropdownOpen]);

    useEffect(() => {
        if (page === 0) return;
        fetchSearchResults(page, searchTerm);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                inputRef.current &&
                !inputRef.current.contains(event.target as Node)
            ) {
                setDropdownOpen(false);
                setResults([]);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleInputClick = () => {
        setDropdownOpen(true);
        setLoading(true);
        setPage(0);
    };

    const lastElementRef = useCallback(
        (node: HTMLLIElement | null) => {
            if (loading) return;
            if (observer.current) observer.current.disconnect();
            if (node && hasMore) {
                observer.current = new IntersectionObserver((entries) => {
                    if (entries[0].isIntersecting) {
                        setPage((prevPage) => prevPage + 1);
                    }
                });
                observer.current.observe(node);
            }
        },
        [loading, hasMore]
    );

    const handleItemClick = (path: string) => {
        setSearchTerm("");
        setDropdownOpen(false);
        setResults([]);
        navigate(path);
    };

  
    return (
        <header className="w-full bg-white shadow-md px-4 py-3">
            <div className="flex flex-wrap md:flex-nowrap items-center justify-between gap-2 w-full">
                <div className="flex items-center justify-between w-full md:w-auto">
                    <NavLink to="/">
                        <img
                            className="h-10 md:h-[70px] w-auto"
                            src="../images/logo-backgroundless.png"
                            alt="logo"
                        />
                    </NavLink>

                    <nav className="flex gap-2 md:ml-4 md:gap-4 order-2 md:order-1">
                        <a
                            href="#/help"
                            className="text-gray-700 text-sm md:text-base font-medium hover:text-blue-600"
                        >
                            Help Dashboard
                        </a>
                        <a
                            href="#/docs/introduction"
                            className="text-gray-700 text-sm md:text-base font-medium hover:text-blue-600"
                        >
                            Docs
                        </a>
                    </nav>
                </div>

                <div className="relative w-full md:w-auto max-w-full md:max-w-xs order-1 md:order-2">
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="Search..."
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary text-sm md:text-base"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onClick={handleInputClick}
                    />

                    {dropdownOpen && (
                        <div
                            ref={dropdownRef}
                            className="absolute left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-white border border-gray-300 rounded-md shadow-lg z-50"
                        >
                            <ul className="divide-y divide-gray-200">
                                {loading && results.length === 0 && (
                                    <li className="px-4 py-2 text-center text-gray-500">Loading...</li>
                                )}

                                {results.map((item, idx) => (
                                    <li
                                        key={item.id}
                                        ref={results.length === idx + 1 ? lastElementRef : undefined}
                                        className="hover:bg-gray-100 cursor-pointer"
                                        onClick={() => handleItemClick(item.path)}
                                    >
                                        <div className="block px-4 py-2">
                                            <span className="font-medium">
                                                {item.keywords
                                                    .split(" ")
                                                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                                    .join(" ")}
                                            </span>
                                        </div>
                                    </li>
                                ))}

                                {loading && results.length > 0 && (
                                    <li className="px-4 py-2 text-center text-gray-500">Loading...</li>
                                )}

                                {!loading && results.length === 0 && (
                                    <li className="px-4 py-2 text-center text-gray-500">No results found.</li>
                                )}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default HelpDocHeader;
